'use client';

import Link from 'next/link';
import { IconStarFilled } from '@tabler/icons-react';
import { Badge, Card, Group, Image, Stack, Text } from '@mantine/core';
import {
  PaymentMethod,
  ServiceMethod,
} from '@/src/models/marketplace-service-info';
import { MServiceItem } from '@/src/models/produk-kesehatan';
import { toTitleCase } from '@/src/shared/utils/common';

interface ServiceCardProps {
  service: MServiceItem;
}

export function ServiceCard({ service }: ServiceCardProps) {
  function checkBadge(row: MServiceItem) {
    const badges = [];
    if (row.service_method?.includes(ServiceMethod.HomeCare)) {
      badges.push({ label: 'Home Care', color: 'blue', variant: 'light' });
    }
    if (row.service_method?.includes(ServiceMethod.Online)) {
      badges.push({ label: 'Online', color: 'cyan', variant: 'light' });
    }
    if (row.service_method?.includes(ServiceMethod.Offline)) {
      badges.push({ label: 'Offline', color: 'red', variant: 'light' });
    }
    if (row.payment_method?.includes(PaymentMethod.Umum)) {
      badges.push({ label: 'Umum', color: 'green', variant: 'light' });
    }
    if (row.payment_method?.includes(PaymentMethod.Bpjs)) {
      badges.push({ label: 'BPJS', color: 'green', variant: 'light' });
    }
    if (row.payment_method?.includes(PaymentMethod.Asuransi)) {
      badges.push({ label: 'Asuransi', color: 'green', variant: 'light' });
    }

    return badges;
  }

  return (
    <Card
      padding="0"
      radius="md"
      mt="md"
      component={Link}
      href={`/detail-item/services/${service.bpis_id}?individual_id=${service.individual_id}`}
      className="cursor-pointer"
      withBorder
    >
      <Image
        src={service.doctor_image}
        alt={service.doctor_name}
        h={{
          base: 140,
          sm: 220,
        }}
        fit="contain"
        fallbackSrc="/images/logo.png"
        className="rounded-t-md"
      />

      <Stack gap="xs" my="sm" px={{ base: 'sm', md: 'md' }}>
        <Group gap="0.25rem" className="min-h-10" align="start" my="sm">
          {checkBadge(service).map((badge) => (
            <Badge
              key={badge.label}
              color={badge.color}
              variant={badge.variant as any}
              size="sm"
            >
              {badge.label}
            </Badge>
          ))}
          {service.is_event && (
            <Badge color="yellow" variant="light" size="sm">
              Event
            </Badge>
          )}
        </Group>

        <Stack gap="0.25rem">
          <Stack gap={0}>
            <Text lineClamp={2} fz="sm" fw={500}>
              {toTitleCase(service.service_name)}
            </Text>

            <Text c="gray.6" fz="xs" lineClamp={1}>
              Oleh {toTitleCase(service.doctor_name)}
            </Text>
          </Stack>
          <Text fw="bold" fz="sm" className="min-h-10">
            {service.price_range}
          </Text>
        </Stack>

        <Stack gap={0}>
          <Group gap={6}>
            <IconStarFilled size={15} color="gold" />
            <Text c="dimmed" size="xs">
              {service.star_rating} | {service.transaction_count} terjual
            </Text>
          </Group>
          <Text size="xs" lineClamp={1} c="dimmed">
            {service.outlet_name}
          </Text>
          <Text size="xs" c="dimmed">
            ± {service.distance_string || '0'}
          </Text>
        </Stack>
      </Stack>
    </Card>
  );
}
